import { useEffect, useState } from "react";
import axios from "axios";
import { CheckCircle2, Clock, Loader2, DoorOpen, AlertTriangle, CalendarDays, User, Archive } from "lucide-react";

const pub = axios.create({ baseURL: (process.env.REACT_APP_BACKEND_URL || "") + "/api" });
const fmt = (s) => s ? new Date(s).toLocaleString(undefined, { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "";

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div data-testid="exit-status-page" className="w-full max-w-lg bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
        <div className="brand-grad px-6 py-4 flex items-center gap-2 text-white">
          <DoorOpen size={20} /><span className="font-bold text-lg">Kairos · Request status</span>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}

export default function ExitStatus({ token }) {
  const isSample = token === "sample-preview";
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (isSample) return;
    pub.get(`/exits/status/${token}`).then((r) => setData(r.data)).catch((e) => setErr(e?.response?.data?.detail || "This link is not valid"));
  }, [token, isSample]);

  if (isSample) return (
    <Shell><div data-testid="exit-status-sample" className="text-center text-gray-500">
      <p className="font-bold text-gray-800 mb-1">This is just a sample link</p>
      <p className="text-sm">Real offboarding emails carry a unique link to the actual request status.</p>
    </div></Shell>
  );
  if (err && !data) return <Shell><div className="text-center text-rose-600 flex flex-col items-center gap-2"><AlertTriangle size={28} /><p className="font-bold">{err}</p></div></Shell>;
  if (!data) return <Shell><div className="py-8 grid place-items-center text-gray-300"><Loader2 size={24} className="animate-spin" /></div></Shell>;
  if (data.purged) return (
    <Shell><div data-testid="exit-purged" className="text-center flex flex-col items-center gap-2">
      <div className="w-14 h-14 rounded-full bg-gray-100 text-gray-400 flex items-center justify-center mb-1"><Archive size={28} /></div>
      <p className="font-bold text-gray-800">This request has been purged</p>
      <p className="text-sm text-gray-500">There's nothing to see here — this exit was removed and no action is needed.</p>
    </div></Shell>
  );

  const p = data.progress || { confirmed: 0, total: 0 };
  const pct = p.total ? Math.round((p.confirmed / p.total) * 100) : 0;
  const complete = data.exit_status === "completed";

  return (
    <Shell>
      <div className="flex items-center gap-3 mb-4">
        <span className="w-10 h-10 rounded-xl bg-indigo-50 text-indigo-500 flex items-center justify-center shrink-0"><User size={18} /></span>
        <div className="min-w-0">
          <div className="text-xl font-bold text-gray-900 truncate" data-testid="status-employee">{data.employee_name}</div>
          <div className="text-xs text-gray-400">
            {data.location && <span>{data.location}</span>}
            {data.raised_by_name && <span> · raised by {data.raised_by_name}</span>}
          </div>
        </div>
        <span data-testid="status-pill" className={`ml-auto inline-flex items-center gap-1 text-[11px] font-bold px-2.5 py-1 rounded-full ${complete ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-amber-50 text-amber-700 border border-amber-200"}`}>
          {complete ? <CheckCircle2 size={12} /> : <Clock size={12} />} {complete ? "Completed" : "In progress"}
        </span>
      </div>

      {(data.resignation_date || data.last_working_day) && (
        <div className="flex items-center gap-3 flex-wrap text-[11px] text-gray-500 mb-4">
          {data.resignation_date && <span className="inline-flex items-center gap-1"><CalendarDays size={11} /> Resigned {data.resignation_date}</span>}
          {data.last_working_day && <span className="inline-flex items-center gap-1"><CalendarDays size={11} /> Last day {data.last_working_day}</span>}
        </div>
      )}

      <div className="mb-5">
        <div className="flex items-center justify-between text-xs font-bold text-gray-500 mb-1">
          <span>Overall progress</span>
          <span className="tabnum text-gray-600" data-testid="status-progress">{p.confirmed}/{p.total} confirmed</span>
        </div>
        <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
          <div className="h-full bg-indigo-500 transition-all" style={{ width: `${pct}%` }} />
        </div>
      </div>

      <div className="space-y-2">
        {(data.sections || []).map((s) => {
          const done = s.status === "confirmed";
          return (
            <div key={s.id} data-testid={`status-section-${s.id}`} className={`rounded-xl border p-3 flex items-start gap-3 ${done ? "border-emerald-100 bg-emerald-50/40" : "border-gray-100 bg-gray-50/50"}`}>
              <span className={`mt-0.5 w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${done ? "bg-emerald-100 text-emerald-600" : "bg-gray-200 text-gray-400"}`}>
                {done ? <CheckCircle2 size={13} /> : <Clock size={13} />}
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-bold text-sm text-gray-800">{s.name}</span>
                  {s.required && <span className="text-[9px] font-bold uppercase tracking-wider text-indigo-500 bg-indigo-50 rounded px-1">required</span>}
                </div>
                {s.action && <div className="text-xs text-gray-500">{s.action}</div>}
                {done ? (
                  <div className="text-[11px] text-emerald-700 mt-1">
                    Confirmed by <b>{s.confirmed_by_name || "—"}</b>{s.confirmed_at ? ` · ${fmt(s.confirmed_at)}` : ""}
                    {s.note && <span className="block text-emerald-600 mt-0.5">“{s.note}”</span>}
                  </div>
                ) : (
                  <div className="text-[11px] text-amber-600 mt-1 font-semibold">Still pending</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </Shell>
  );
}
